import { useState, useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import type { Note } from "@gbougakov/brief-ts";
import { useBrief } from "@gbougakov/brief-ts/react";
import { TagChips } from "./TagChips";

interface VoiceNoteCardProps {
  note: Note;
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function VoiceNoteCard({ note }: VoiceNoteCardProps) {
  const client = useBrief();
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const loadAndPlay = async () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        audioRef.current.play();
        setPlaying(true);
      }
      return;
    }

    setLoading(true);
    try {
      const attachments = await client.getAttachments(note.id);
      const voiceAttachment = attachments.find((a) =>
        a.contentType.startsWith("audio/"),
      );
      if (!voiceAttachment) return;

      const data = await client.downloadAttachment(voiceAttachment.id);
      const blob = new Blob([data], { type: voiceAttachment.contentType });
      const url = URL.createObjectURL(blob);

      const audio = new Audio(url);
      audioRef.current = audio;

      audio.addEventListener("loadedmetadata", () => {
        setDuration(audio.duration);
      });
      audio.addEventListener("timeupdate", () => {
        if (audio.duration) {
          setProgress(audio.currentTime / audio.duration);
        }
      });
      audio.addEventListener("ended", () => {
        setPlaying(false);
        setProgress(0);
      });

      await audio.play();
      setPlaying(true);
    } catch (e) {
      console.error("Failed to play voice note:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <div className="bg-surface-container-high rounded-2xl overflow-hidden p-4 flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <button
          onClick={loadAndPlay}
          disabled={loading}
          className="shrink-0 w-10 h-10 rounded-full bg-primary flex items-center justify-center"
        >
          {loading ? (
            <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
          ) : playing ? (
            <Pause className="w-5 h-5 text-primary-foreground" />
          ) : (
            <Play className="w-5 h-5 text-primary-foreground ml-0.5" />
          )}
        </button>

        <div className="flex-1 flex items-center gap-1.5 pl-0.5">
          <div className="flex-1 h-1 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-100"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>

        <span className="text-base leading-4 text-outline shrink-0">
          {duration > 0 ? formatDuration(duration) : formatTime(note.createdAt)}
        </span>
      </div>

      {note.transcription && (
        <p className="text-base leading-6 text-secondary whitespace-pre-wrap break-words">
          {note.transcription}
        </p>
      )}

      <TagChips noteId={note.id} />

      <span className="text-base leading-4 text-outline self-start">
        {formatTime(note.createdAt)}
      </span>
    </div>
  );
}
